import { Component, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild } from '@angular/core'
import { Checkout, Currency, Customer, Depot, Product, SellItem, Supplier } from 'depoto-core/src/entities'
import { ModalContainerComponent } from '../../_common/modal-container/modal-container.component'
import { ProductPriceLevelUtil } from 'depoto-core/src/utils/ProductPriceLevelUtil'
import { CoreService } from '../../../services'
import { SchemaUtil } from '../../../utils'

@Component({
  selector: 'modal-stock-overview',
  templateUrl: 'modal-stock-overview.component.html',
  styleUrls: ['modal-stock-overview.component.scss'],
})
export class ModalStockOverviewComponent implements OnInit {
  @ViewChild('modalStock') public childModal: ModalContainerComponent
  @Input() public product: Product
  @Input() public checkout: Checkout
  @Input() public depots: Depot[]
  @Input() public suppliers: Supplier[]
  @Input() public filterDepotIdFrom: number
  @Input() public filterDepots: number[] = []
  @Input() public filterSuppliers: number[] = []
  @Input() public isSmallBtn = false
  @Input() public isSumCalculated = false // only on batch stock load, product list..
  @Input() public isCalculatedOfProductDepots = false
  @Input() public isFullWidth = false
  @Input() public customer: Customer
  @Input() public isQuantityUnderMinimum: boolean
  @Input() public isSomeOfItemsExpired: boolean
  @Input() public isSpecialAddOrderItemsInstance = false
  @Input() public total: number
  @Output() public onSelectedProductDepot: EventEmitter<any> = new EventEmitter()
  stock: any[] = []
  productPrice = 0
  sellItems: SellItem[] = []
  totalQuantityStock = 0
  totalQuantityReservation = 0
  totalQuantityAvailable = 0
  isLoading = true
  purchaseCurrencyFromCompany: Currency // there is no way to change this across hs, so it's certain we can use this

  constructor(private core: CoreService) {
    this.purchaseCurrencyFromCompany = this.core.services.user.user.company.currency
  }

  ngOnInit() {
    this.productPrice = ProductPriceLevelUtil.getProductPriceForCustomerPriceLevel(this.product, this.customer)
    this.isLoading = false
  }

  private async getSellItems() {
    this.isLoading = true
    this.sellItems = await this.core.services.product
      .getProductSellItems(this.product.id, this.checkout ? this.checkout.id : null, null, SchemaUtil.sellItem)
      .then(result => result.items.map(sellItem => new SellItem(sellItem)))
    this.totalQuantityAvailable = Object.values(this.sellItems).reduce(
      (total, { quantityAvailable }) => total + quantityAvailable,
      0,
    )
    this.totalQuantityReservation = Object.values(this.sellItems).reduce(
      (total, { quantityReservation }) => total + quantityReservation,
      0,
    )
    this.totalQuantityStock = Object.values(this.sellItems).reduce(
      (total, { quantityStock }) => total + quantityStock,
      0,
    )
    this.isLoading = false
  }

  public showChildModal(): void {
    this.isCalculatedOfProductDepots ? this.formatStockDetail() : this.getSellItems()
    this.childModal.showChildModal()
  }

  public hideChildModal(): void {
    this.childModal.hideChildModal()
  }

  async formatStock(): Promise<void> {
    const stock = []
    // @ts-ignore
    for (const q of this.product.quantities) {
      stock.push({
        depot: q.depot,
        items: [],
        sumAll: q.quantityStock,
        sumReservations: q.quantityReservation,
        sumAvailable: q.quantityAvailable,
      })
      this.totalQuantityStock = Number(this.totalQuantityStock) + Number(q.quantityStock)
      this.totalQuantityReservation = Number(this.totalQuantityReservation) + Number(q.quantityReservation)
      this.totalQuantityAvailable = Number(this.totalQuantityAvailable) + Number(q.quantityAvailable)
    }
    this.stock = stock
  }

  async formatStockDetail(): Promise<void> {
    if (!this.product.productDepots || (this.product.productDepots && !this.product.productDepots.length)) {
      this.isLoading = true
      const r = await this.core.services.product.getById(this.product.id, {
        id: null,
        productDepots: {
          id: null,
          depot: { id: null, name: null },
          supplier: { id: null, name: null },
          purchasePrice: null,
          quantityStock: null,
          quantityReservation: null,
          quantityAvailable: null,
          inventoryQuantityStock: null,
          batch: null,
          expirationDate: null,
          position: null,
          position1: null,
          position2: null,
          position3: null,
          created: null,
          updated: null,
        },
      })
      this.product.productDepots = [...r.productDepots]
      this.isLoading = false
    }
    const filterDepots: number[] = [...this.filterDepots]
    if (this.filterDepotIdFrom && this.filterDepotIdFrom > 0) {
      filterDepots.push(Number(this.filterDepotIdFrom))
    }
    const filterSuppliers: number[] = [...this.filterSuppliers]
    const stock: any[] = []
    this.totalQuantityStock = 0
    this.totalQuantityReservation = 0
    this.totalQuantityAvailable = 0
    if (this.depots?.length) {
      this.depots.forEach(d => {
        stock.push({
          depot: d,
          items: [],
          sumAll: 0,
          sumReservations: 0,
          sumAvailable: 0,
        })
      })
    }
    if (this.product?.productDepots?.length) {
      this.product.productDepots.forEach(di => {
        if (
          (!filterDepots || filterDepots.length === 0 || (filterDepots && filterDepots.indexOf(di.depot?.id) !== -1)) &&
          (!filterSuppliers ||
            filterSuppliers.length === 0 ||
            (filterSuppliers && filterSuppliers.indexOf(di.supplier?.id) !== -1))
        ) {
          this.totalQuantityStock = Number(this.totalQuantityStock) + Number(di.quantityStock)
          this.totalQuantityReservation = Number(this.totalQuantityReservation) + Number(di.quantityReservation)
          this.totalQuantityAvailable =
            Number(this.totalQuantityAvailable) + Number(di.quantityStock - di.quantityReservation)
          stock.forEach((si, i) => {
            if (di.depot.id === si.depot.id) {
              si.items.push(di)
              stock[i].sumAll += Number(di.quantityStock)
              stock[i].sumReservations += Number(di.quantityReservation)
              stock[i].sumAvailable =
                stock[i].sumAll - stock[i].sumReservations >= 0 ? stock[i].sumAll - stock[i].sumReservations : 0
            }
          })
        }
      })
    } else {
      this.totalQuantityAvailable = this.product.quantityAvailable
      this.totalQuantityReservation = this.product.quantityReservation
      this.totalQuantityStock = this.product.quantityStock
    }

    if (this.totalQuantityAvailable < 0) {
      this.totalQuantityAvailable = 0
    }
    this.stock = stock
  }
}
